import { Injectable } from '@angular/core';

/**
 * Cache entry with timestamp for TTL management
 */
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

/**
 * Simple in-memory cache service for blog post search results
 * 
 * Features:
 * - Time-based expiration (TTL)
 * - Automatic cleanup of expired entries
 * - Size-based eviction (LRU-like)
 * - Key generation based on search criteria
 * 
 * @example
 * ```typescript
 * const cacheKey = this.cache.generateKey({ search: 'angular', orgId: '123' });
 * const cached = this.cache.get(cacheKey);
 * 
 * if (!cached) {
 *   const results = await fetchResults();
 *   this.cache.set(cacheKey, results);
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class BlogPostSearchCacheService {
  private cache = new Map<string, CacheEntry<any>>();
  
  /**
   * Default TTL: 5 minutes (in milliseconds)
   */
  private readonly DEFAULT_TTL = 5 * 60 * 1000;
  
  /**
   * Maximum cache size (number of entries)
   */
  private readonly MAX_CACHE_SIZE = 50;

  /**
   * Generate a cache key from search criteria
   */
  generateKey(criteria: Record<string, any>): string {
    // Sort keys for consistent key generation
    const sortedKeys = Object.keys(criteria).sort();
    const keyParts = sortedKeys.map(key => {
      const value = criteria[key];
      if (Array.isArray(value)) {
        return `${key}:${value.sort().join(',')}`;
      }
      return `${key}:${value}`;
    });
    return keyParts.join('|');
  }

  /**
   * Get cached data if not expired
   * 
   * @param key - Cache key
   * @param ttl - Time to live in milliseconds (default: 5 minutes)
   * @returns Cached data or null if not found/expired
   */
  get<T>(key: string, ttl: number = this.DEFAULT_TTL): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      return null;
    }
    
    const now = Date.now();
    const isExpired = now - entry.timestamp > ttl;
    
    if (isExpired) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data as T;
  }

  /**
   * Set cache entry
   * 
   * @param key - Cache key
   * @param data - Data to cache
   */
  set<T>(key: string, data: T): void {
    // If cache is full, remove oldest entry (simple LRU)
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }
    
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Check if a key exists and is not expired
   */
  has(key: string, ttl: number = this.DEFAULT_TTL): boolean {
    return this.get(key, ttl) !== null;
  }

  /**
   * Clear specific cache entry
   */
  delete(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Clear all expired entries
   */
  clearExpired(ttl: number = this.DEFAULT_TTL): void {
    const now = Date.now();
    const keysToDelete: string[] = [];
    
    this.cache.forEach((entry, key) => {
      if (now - entry.timestamp > ttl) {
        keysToDelete.push(key);
      }
    });
    
    keysToDelete.forEach(key => this.cache.delete(key));
  }

  /**
   * Get current cache size
   */
  size(): number {
    return this.cache.size;
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; maxSize: number; entries: string[] } {
    return {
      size: this.cache.size,
      maxSize: this.MAX_CACHE_SIZE,
      entries: Array.from(this.cache.keys())
    };
  }
}
